import type { ReactNode } from "react"
import { createContext, useContext, useSyncExternalStore } from "react"

import { accessStore } from "./access-store"
import type { AccessReady, AccessState, SectionAccess } from "./access-state"
import { evaluateSection } from "./access-state"
import type { KnownPermission, PermissionSet } from "./permissions"
import { hasPermission } from "./permissions"

/** Состояние доступа целиком. Годится везде, в том числе на экране входа. */
export function useAccessState(): AccessState {
  return useSyncExternalStore(accessStore.subscribe, accessStore.getState)
}

export function useSectionAccess(required: KnownPermission | null): SectionAccess {
  return evaluateSection(useAccessState(), required)
}

const ReadyContext = createContext<AccessReady | null>(null)

/**
 * Загруженный профиль для поддерева защищённых экранов.
 *
 * Провайдер ставится ровно там, где проверка уже прошла. Тогда экрану не нужно каждый раз
 * различать «ещё грузится» и «уже загружено»: внутри защищённого экрана профиль ЕСТЬ
 * по построению, и это видно в типах.
 */
export function AccessReadyProvider(props: {
  readonly access: AccessReady
  readonly children: ReactNode
}): ReactNode {
  return <ReadyContext value={props.access}>{props.children}</ReadyContext>
}

export function useCabinetAccess(): AccessReady {
  const access = useContext(ReadyContext)
  if (access === null) {
    // Это ошибка разработки, а не состояние интерфейса: экран поставили вне защищённой
    // области, и он готов судить о правах раньше, чем они загружены.
    throw new Error("useCabinetAccess вызван вне защищённого экрана")
  }
  return access
}

export function usePermissions(): PermissionSet {
  return useCabinetAccess().permissions
}

/** Проверка права внутри защищённого экрана: прятать кнопку, а не весь раздел. */
export function useHasPermission(code: KnownPermission): boolean {
  return hasPermission(usePermissions(), code)
}
